import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Maximize2, Moon, Plus, RefreshCcw, Volume2, VolumeX, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getMyDevices, getCameraStream } from '../../api/devices';
interface BabyCameraCardProps {
  token: string | null;
  cameraFrame: string | null;
  cameraStatus: {
    isLive: boolean;
    lastOnlineAt: string | null;
  } | null;
}
function timeAgo(iso: string | null): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60000) return 'just now';
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}
export function BabyCameraCard({
  token,
  cameraFrame,
  cameraStatus
}: BabyCameraCardProps) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isLinked, setIsLinked] = useState<boolean>(false);
  const [cameraName, setCameraName] = useState<string>('Baby Camera');
  const [initialLastOnline, setInitialLastOnline] = useState<string | null>(null);
  const [initialIsLive, setInitialIsLive] = useState<boolean>(false);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const fetchedAt = useRef<number>(0);
  // Fetch camera metadata for the user's primary device.
  useEffect(() => {
    let cancelled = false;
    const fetchCamera = async () => {
      if (!token) {
        setIsLoading(false);
        return;
      }
      try {
        const devices = await getMyDevices(token);
        if (!Array.isArray(devices) || devices.length === 0) {
          if (!cancelled) {
            setIsLinked(false);
            setIsLoading(false);
          }
          return;
        }
        const primary = devices[0];
        const deviceId = primary?.id || primary?.deviceUid;
        if (!deviceId) {
          if (!cancelled) {
            setIsLinked(false);
            setIsLoading(false);
          }
          return;
        }
        try {
          const cam = await getCameraStream(token, deviceId);
          if (cancelled) return;
          if (!cam || cam.isLinked === false) {
            setIsLinked(false);
          } else {
            setIsLinked(true);
            setCameraName(cam.cameraName || 'Baby Camera');
            setInitialIsLive(!!cam.isLive);
            setInitialLastOnline(cam.lastOnlineAt || null);
          }
        } catch (err: any) {
          // 404 = camera not linked, treat as unlinked
          if (!cancelled) setIsLinked(false);
        }
      } catch (err) {
        if (!cancelled) setIsLinked(false);
      } finally {
        if (!cancelled) setIsLoading(false);
        fetchedAt.current = Date.now();
      }
    };
    fetchCamera();
    return () => {
      cancelled = true;
    };
  }, [token]);
  // Effective live status: prefer WS status if we have one, fall back to initial fetch.
  const isLive = useMemo(() => {
    if (cameraStatus) return cameraStatus.isLive;
    return initialIsLive;
  }, [cameraStatus, initialIsLive]);
  // Effective last-online: prefer fresh WS data
  const lastOnlineAt = cameraStatus?.lastOnlineAt ?? initialLastOnline;
  // Close fullscreen on Esc
  useEffect(() => {
    if (!isFullscreen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isFullscreen]);
  // ─── Loading skeleton ────────────────────────────────────────────────
  if (isLoading) {
    return <div className="bg-white rounded-[24px] p-5 shadow-card border border-border/50" data-id="element-716">
        <div className="aspect-video w-full rounded-[18px] bg-secondary animate-pulse" data-id="element-717" />
      </div>;
  }
  // ─── Not linked ──────────────────────────────────────────────────────
  if (!isLinked) {
    return <div className="bg-white rounded-[24px] p-6 shadow-card border border-border/50" data-id="element-718">
        <div className="flex items-center gap-4" data-id="element-719">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0" data-id="element-720">
            <Camera size={26} className="text-primary" data-id="element-721" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-722">
            <h2 className="text-[16px] font-semibold text-text-primary mb-1" data-id="element-723">
              Add your baby camera
            </h2>
            <p className="text-[13px] text-text-secondary" data-id="element-724">
              Watch a live view of your little one whenever you want.
            </p>
          </div>
          <button onClick={() => navigate('/app/profile/devices')} className="flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-pill bg-primary text-white text-[13px] font-semibold hover:bg-primary/90 transition-colors" data-id="element-725">
            <Plus size={16} data-id="element-726" />
            Set up
          </button>
        </div>
      </div>;
  }
  // ─── Offline ─────────────────────────────────────────────────────────
  if (!isLive) {
    return <div className="bg-white rounded-[24px] p-6 shadow-card border border-border/50" data-id="element-727">
        <div className="flex items-center gap-4" data-id="element-728">
          <div className="w-14 h-14 rounded-full bg-secondary flex items-center justify-center flex-shrink-0 relative" data-id="element-729">
            <Moon size={24} className="text-text-secondary" data-id="element-730" />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-border" data-id="element-731" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-732">
            <div className="flex items-center gap-2 mb-1" data-id="element-733">
              <h2 className="text-[16px] font-semibold text-text-primary truncate" data-id="element-734">
                {cameraName}
              </h2>
              <span className="text-[11px] font-semibold uppercase tracking-wider text-text-secondary bg-border/60 px-2 py-0.5 rounded-pill" data-id="element-735">
                Offline
              </span>
            </div>
            <p className="text-[13px] text-text-secondary" data-id="element-736">
              {lastOnlineAt ? `Last seen ${timeAgo(lastOnlineAt)}` : 'Camera is offline'}
            </p>
          </div>
        </div>
      </div>;
  }
  // ─── Live ────────────────────────────────────────────────────────────
  return <>
      <div className="bg-white rounded-[24px] p-3 shadow-card border border-border/50" data-id="element-737">
        <button onClick={() => setIsFullscreen(true)} className="relative w-full block rounded-[18px] overflow-hidden bg-black group" aria-label="Expand camera to fullscreen" data-id="element-738">
          <div className="aspect-video w-full bg-black flex items-center justify-center" data-id="element-739">
            {cameraFrame ? <img src={`data:image/jpeg;base64,${cameraFrame}`} alt="Live baby camera" className="w-full h-full object-cover" data-id="element-740" /> : <div className="flex flex-col items-center text-white/70" data-id="element-741">
                <motion.div animate={{
              opacity: [0.4, 1, 0.4]
            }} transition={{
              duration: 1.6,
              repeat: Infinity
            }} data-id="element-742">
                  <RefreshCcw size={28} data-id="element-743" />
                </motion.div>
                <span className="text-[12px] mt-2 tracking-wide" data-id="element-744">
                  Connecting…
                </span>
              </div>}
          </div>

          {/* LIVE pill */}
          <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-black/55 backdrop-blur-sm text-white text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-pill" data-id="element-745">
            <span className="relative flex h-2 w-2" data-id="element-746">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-danger opacity-75" data-id="element-747" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-danger" data-id="element-748" />
            </span>
            Live
          </div>

          {/* Expand hint */}
          <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/55 backdrop-blur-sm text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-749">
            <Maximize2 size={14} data-id="element-750" />
          </div>
        </button>

        <div className="flex items-center justify-between px-2 pt-3 pb-1" data-id="element-751">
          <div className="min-w-0" data-id="element-752">
            <p className="text-[14px] font-semibold text-text-primary truncate" data-id="element-753">
              {cameraName}
            </p>
            <p className="text-[12px] text-text-secondary" data-id="element-754">Streaming live</p>
          </div>
          <button onClick={() => setSoundEnabled(s => !s)} className="w-10 h-10 rounded-full bg-secondary text-primary flex items-center justify-center hover:bg-primary/10 transition-colors flex-shrink-0" aria-label={soundEnabled ? 'Mute camera' : 'Unmute camera'} title="Audio support coming soon" data-id="element-755">
            {soundEnabled ? <Volume2 size={18} data-id="element-756" /> : <VolumeX size={18} data-id="element-757" />}
          </button>
        </div>
      </div>

      {/* Fullscreen overlay */}
      <AnimatePresence data-id="element-758">
        {isFullscreen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[200] bg-black flex flex-col" data-id="element-759">
            <button onClick={() => setIsFullscreen(false)} className="absolute top-4 right-4 z-10 w-11 h-11 rounded-full bg-white/15 backdrop-blur-md text-white flex items-center justify-center hover:bg-white/25 transition-colors" aria-label="Close fullscreen" data-id="element-760">
              <X size={22} data-id="element-761" />
            </button>

            <div className="absolute top-5 left-4 z-10 inline-flex items-center gap-1.5 bg-black/55 backdrop-blur-sm text-white text-[12px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-pill" data-id="element-762">
              <span className="relative flex h-2 w-2" data-id="element-763">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-danger opacity-75" data-id="element-764" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-danger" data-id="element-765" />
              </span>
              Live · {cameraName}
            </div>

            <div className="flex-1 flex items-center justify-center" data-id="element-766">
              {cameraFrame ? <img src={`data:image/jpeg;base64,${cameraFrame}`} alt="Live baby camera (fullscreen)" className="max-w-full max-h-full object-contain" data-id="element-767" /> : <div className="flex flex-col items-center text-white/70" data-id="element-768">
                  <motion.div animate={{
              opacity: [0.4, 1, 0.4]
            }} transition={{
              duration: 1.6,
              repeat: Infinity
            }} data-id="element-769">
                    <RefreshCcw size={36} data-id="element-770" />
                  </motion.div>
                  <span className="text-[13px] mt-3 tracking-wide" data-id="element-771">
                    Connecting…
                  </span>
                </div>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}